import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  createTenantAccessOverride,
  getTenantAccessOverrides,
  getTenantAccessStatus,
  revokeTenantAccessOverride,
  runTenantAccessCheck,
} from '../api/tenantAccessApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens } from '../app/designTokens';
import { DetailField } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/Button';
import { Select, SelectOption } from './ui/Select';
import { TextInput } from './ui/TextInput';
import {
  formatAccessResult,
  formatAccessReasonCode,
  formatBillingStatus,
  formatReadinessMessage,
  formatTenantAccessStatus,
  formatUnknownCode,
} from '../utils/systemLabels';

const { semantic } = colorTokens;

const OVERRIDE_TYPE_KEYS = {
  allow: 'override_allow',
  block: 'override_block',
  read_only: 'override_read_only',
};

export function TenantAccessPanel({ tenantId }) {
  const { t } = useTranslation(['tenantAccess', 'common', 'statuses', 'billing']);
  const { session } = useAuth();
  const canOverride = hasPermission(session, 'system.tenant_access.override');

  const [status, setStatus] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [message, setMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ override_type: 'allow', reason: '' });

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [statusResponse, overridesResponse] = await Promise.all([
        getTenantAccessStatus(tenantId),
        getTenantAccessOverrides(tenantId),
      ]);
      setStatus(statusResponse.data || statusResponse);
      setOverrides(overridesResponse.data || []);
    } catch {
      setMessage(t('tenantAccess:load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunCheck = async () => {
    setChecking(true);
    try {
      const response = await runTenantAccessCheck(tenantId, { context: 'manual_check' });
      setStatus(response.data || response);
      setMessage(t('tenantAccess:check_completed'));
      load();
    } catch {
      setMessage(t('tenantAccess:check_failed'));
    } finally {
      setChecking(false);
    }
  };

  const handleCreateOverride = async () => {
    setSavingOverride(true);
    try {
      await createTenantAccessOverride(tenantId, form);
      setDialogOpen(false);
      setMessage(t('tenantAccess:override_created'));
      load();
    } catch {
      setMessage(t('tenantAccess:create_failed'));
    } finally {
      setSavingOverride(false);
    }
  };

  const handleRevoke = async (overrideId) => {
    setRevoking(true);
    try {
      await revokeTenantAccessOverride(tenantId, overrideId, { reason: t('tenantAccess:revoke_reason') });
      setMessage(t('tenantAccess:override_revoked'));
      load();
    } catch {
      setMessage(t('tenantAccess:revoke_failed'));
    } finally {
      setRevoking(false);
    }
  };

  const getOverrideTypeLabel = (type) => t(`tenantAccess:${OVERRIDE_TYPE_KEYS[type] || type}`, type);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const activeOverride = overrides.find((item) => item.is_currently_active);

  return (
    <Stack spacing={2}>
      {message && <Alert severity="info">{message}</Alert>}

      <SectionCard variant="Default" title={t('tenantAccess:access_gate')} sx={{ mb: 0 }}>
        {status ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('tenantAccess:result')}
              value={formatAccessResult(status.result) || status.result}
            />
            <DetailField label={t('common:status')}>
              <StatusBadge
                status={status.access_status}
                category="tenant_access"
                apiLabel={formatTenantAccessStatus(status.access_status)}
                size="Sm"
              />
            </DetailField>
            <DetailField
              label={t('tenantAccess:reason')}
              value={formatAccessReasonCode(status.reason_code) || status.reason_code}
            />
            <DetailField
              label={t('common:message')}
              value={formatReadinessMessage(status.message) || status.message}
            />
            <DetailField label={t('tenantAccess:can_login')}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                <StatusBadge
                  status={status.can_login ? 'yes' : 'no'}
                  category="generic"
                  apiLabel={status.can_login ? t('statuses:yes') : t('statuses:no')}
                  size="Sm"
                  tone={status.can_login ? 'Success' : 'Neutral'}
                />
              </Box>
            </DetailField>
            <DetailField label={t('tenantAccess:requires_payment_action')}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                <StatusBadge
                  status={status.requires_payment_action ? 'yes' : 'no'}
                  category="generic"
                  apiLabel={status.requires_payment_action ? t('statuses:yes') : t('statuses:no')}
                  size="Sm"
                  tone={status.requires_payment_action ? 'Warning' : 'Neutral'}
                />
              </Box>
            </DetailField>
            {status.metadata?.paid_until && (
              <DetailField label={t('billing:paid_until')} value={status.metadata.paid_until} />
            )}
            {status.metadata?.grace_until && (
              <DetailField label={t('billing:grace_until')} value={status.metadata.grace_until} />
            )}
            {status.metadata?.payment_flow_status && (
              <DetailField
                label={t('billing:payment_flow_status')}
                value={
                  formatBillingStatus(status.metadata.payment_flow_status)
                  || formatUnknownCode(status.metadata.payment_flow_status)
                }
              />
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: semantic.text.muted }}>
            {t('common:empty_value')}
          </Typography>
        )}
      </SectionCard>

      {activeOverride && (
        <SectionCard variant="Warning" sx={{ mb: 0 }}>
          <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
            {t('tenantAccess:active_override', {
              type: getOverrideTypeLabel(activeOverride.override_type),
              reason: activeOverride.reason || t('tenantAccess:no_reason'),
            })}
          </Typography>
        </SectionCard>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="Secondary" onClick={handleRunCheck} loading={checking} disabled={checking}>
          {t('tenantAccess:run_access_check')}
        </Button>
        {canOverride && (
          <Button variant="Primary" onClick={() => setDialogOpen(true)}>
            {t('tenantAccess:create_override')}
          </Button>
        )}
        {canOverride && activeOverride && (
          <Button
            variant="Danger"
            onClick={() => handleRevoke(activeOverride.id)}
            loading={revoking}
            disabled={revoking}
          >
            {t('tenantAccess:revoke_override')}
          </Button>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tenantAccess:create_dialog_title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Select
              density="Compact"
              fullWidth
              label={t('tenantAccess:override_type')}
              value={form.override_type}
              onChange={(e) => setForm({ ...form, override_type: e.target.value })}
            >
              <SelectOption value="allow">{t('tenantAccess:override_allow')}</SelectOption>
              <SelectOption value="block">{t('tenantAccess:override_block')}</SelectOption>
              <SelectOption value="read_only">{t('tenantAccess:override_read_only')}</SelectOption>
            </Select>
            <TextInput
              density="Compact"
              label={t('tenantAccess:reason')}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="Ghost" onClick={() => setDialogOpen(false)}>{t('common:cancel')}</Button>
          <Button
            variant="Primary"
            onClick={handleCreateOverride}
            loading={savingOverride}
            disabled={savingOverride}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
